import { FilterColumn } from "../models/FilterColumn";
import { ObjectField } from "../models/ObjectField";
import { FilterControlTypes } from "../constants/FilterControlTypes";
import { FilterOperators } from "../constants/FilterOperators";
import { ObjectFieldDataTypes } from "../constants/ObjectFieldDataTypes";
import { StringFormat } from "../utilities/StringFormat";
import uuid from "uuid";
import { ObjectFieldLabelFormat } from "../utilities/ObjectFieldLabelFormat";

export class FilterColumnBuilder {
    private objectField: ObjectField;
    private filterColumn: FilterColumn;

    constructor(objectField: ObjectField) {
        this.objectField = objectField;
        this.initialize();
    }

    private initialize(): void {
        let operators = this.getFilterOperators()
        this.filterColumn = {
            Id: uuid.v4(),
            Filter: {
                Property: this.objectField.Code,
                Operator: null,
                Value: null,
                IsCustomFilter: this.objectField.IsCustom,
                FilterDataType: this.objectField.DataTypeCode
            },
            Control: {
                Type: this.getFilterControlType(),
                DataSource: this.objectField.LookupObjectTableCode,
                SelectProperty: this.objectField.LookupObjectTable?.IsClosedTable ? "Code" : "Id",
                DisplayProperty: this.objectField.LookupObjectTable?.LookupDisplayObjectFieldCode!
            },
            Operators: operators,
            Label: ObjectFieldLabelFormat.build(this.objectField),
            Data: {
                PropertyValue: null,
                OperatorValue: null,
                FilterValue: null
            },
            ObjectField: this.objectField
        };
    }

    private getFilterOperators(): any[] {
        let operators: any[] = [];
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.String ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.Text) {
            operators = [
                FilterOperators.Equals,
                FilterOperators.NotEquals,
                FilterOperators.Contains,
                FilterOperators.NotContains,
                FilterOperators.StartsWith,
                FilterOperators.EndsWith
            ];
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Integer ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.Decimal ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.Date ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.DateTime) {
            operators = [
                FilterOperators.Equals,
                FilterOperators.NotEquals,
                FilterOperators.GreaterThan,
                FilterOperators.GreaterThanOrEqual,
                FilterOperators.LessThan,
                FilterOperators.LessThanOrEqual
            ];
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.PickList ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.Boolean) {
            operators = [
                FilterOperators.Equals,
                FilterOperators.NotEquals
            ];
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Lookup && 
            !this.objectField.LookupObjectTable?.IsClosedTable) {
            operators = [
                FilterOperators.Equals,
                FilterOperators.NotEquals,
                FilterOperators.InList
            ];
        }

        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Lookup && 
            this.objectField.LookupObjectTable?.IsClosedTable) {
            operators = [
                FilterOperators.Equals,
                FilterOperators.NotEquals
            ];
        }

        return operators.map(operator => {
            return { Code: operator, Name: StringFormat.joinSpaceForCapitalLetters(operator) }
        });
    }

    private getFilterControlType(): string {
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Lookup &&
            !this.objectField.LookupObjectTable?.IsClosedTable) {
            return FilterControlTypes.AutoComplete;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Lookup &&
            this.objectField.LookupObjectTable?.IsClosedTable) {
            return FilterControlTypes.DropDown;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.String ||
            this.objectField.DataTypeCode === ObjectFieldDataTypes.Text) {
            return FilterControlTypes.TextInput;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Integer) {
            return FilterControlTypes.NumberInput;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Decimal) {
            return FilterControlTypes.DecimalInput;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Date) {
            return FilterControlTypes.Calender;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.DateTime) {
            return FilterControlTypes.CalenderWithTime;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.Boolean) {
            return FilterControlTypes.CheckBox;
        }
        if (this.objectField.DataTypeCode === ObjectFieldDataTypes.PickList) {
            return FilterControlTypes.DropDown;
        }

        return FilterControlTypes.TextInput;
    }

    clone(filterColumn: FilterColumn): FilterColumnBuilder {
        this.filterColumn.Filter.Property = null;
        this.filterColumn.Filter.Operator = null;
        this.filterColumn.Filter.Value = null;
        this.filterColumn.Filter.IsCustomFilter = filterColumn.Filter.IsCustomFilter;
        this.filterColumn.Filter.FilterDataType = filterColumn.Filter.FilterDataType;
        this.filterColumn.Control.Type = filterColumn.Control.Type;
        this.filterColumn.Control.DataSource = filterColumn.Control.DataSource;
        this.filterColumn.Control.SelectProperty = filterColumn.Control.SelectProperty;
        this.filterColumn.Control.DisplayProperty = filterColumn.Control.DisplayProperty;
        this.filterColumn.Operators = filterColumn.Operators;
        this.filterColumn.Label = filterColumn.Label;
        this.filterColumn.ObjectField = filterColumn.ObjectField;
        return this;
    }

    build(): FilterColumn {
        return this.filterColumn;
    }
}